import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from "../../utils/axios";
import { getToken, getUser } from '../../utils/Common';
import Layout from '../../components/Layout';
import { FileText, ClipboardList, ShoppingCart, RefreshCw } from 'lucide-react';

console.log('📦 Daily Module - File imported and parsed');

function Daily() {
  console.log('🟣🟣🟣 Daily Component - Component Loaded!');
  console.log('🟣 Daily Component - Function executing at:', new Date().toISOString());
  
  const token = getToken();
  console.log('🟣 Daily Component - Token:', token ? 'Present' : 'Missing');
  
  // Session (encrypted)
  const userData = getUser() || {};
  const hcode = userData.hotel_id;
  console.log('🟣 Daily Component - User Data:', { userData, hcode });

  // Memoize headers to prevent recreation on every render
  const headers = useMemo(() => ({ 
    Authorization: `Bearer ${token}`,
    'Access-Control-Allow-Origin': '*', 
    'Content-Type': 'application/json' 
  }), [token]);

  // States
  const [hotelList, setHotelList] = useState([]);
  const [dataReportbyHotel, setDataReportbyHotel] = useState(null);
  const [selectedTab, setSelectedTab] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingHotels, setLoadingHotels] = useState(true);

  // Fetch hotels list
  const getHotels = useCallback(() => {
    if (!hcode || !token) {
      setLoadingHotels(false);
      return;
    }

    setLoadingHotels(true);
    
    axios
      .post('/list/hotel/by-user', { hcode }, { headers })
      .then(function (response) {
        console.log('🔵 getHotels - API Response:', response);
        console.log('🔵 getHotels - Response Data:', response.data);
        console.log('🔵 getHotels - Is Array?', Array.isArray(response.data));
        if (response.data && Array.isArray(response.data)) {
          // Flatten nested arrays if needed (API might return array of arrays)
          const flattenedHotels = response.data.flat();
          console.log('🔵 getHotels - Flattened hotels:', flattenedHotels);
          console.log('🔵 getHotels - First hotel:', flattenedHotels[0]);
          setHotelList(flattenedHotels);
        } else {
          console.warn('🔵 getHotels - Invalid response format, setting empty array');
          setHotelList([]);
        }
      })
      .catch(function (error) {
        console.error('❌ getHotels - Error fetching hotels:', error);
        console.error('❌ getHotels - Error response:', error.response);
        setHotelList([]);
      })
      .finally(() => {
        setLoadingHotels(false);
      });
  }, [hcode, token, headers]);

  // Fetch data count by hotel
  const getDataCountHotel = useCallback(() => {
    if (!hcode || !token) {
      return;
    }

    setLoading(true);
    console.log('🟢 getDataCountHotel - Starting request with hcode:', hcode);
    axios
      .post(`/count/data/hotel`, { hcode }, { headers })
      .then(function (response) {
        console.log('🟢 getDataCountHotel - API Response:', response);
        console.log('🟢 getDataCountHotel - Response Data:', response.data);
        console.log('🟢 getDataCountHotel - Data Type:', typeof response.data);
        console.log('🟢 getDataCountHotel - Is Object?', typeof response.data === 'object' && response.data !== null);
        
        if (response.data && typeof response.data === 'object') {
          const rawData = response.data;
          const transformedData = {};
          
          // Transform data to handle both formats (backward compatibility):
          // Old format: { "HOTEL NAME": [0, 1, 2, 3, ...] } - flat array with hotel name key
          // New format: { [id_hotel]: { pp: {...}, pr: {...}, po: {...} } } - structured object with hotel ID key
          
          Object.keys(rawData).forEach(key => {
            const value = rawData[key];
            
            // Check if it's old format (array of numbers)
            if (Array.isArray(value) && value.length >= 15) {
              // Old format: transform array to structured object
              transformedData[key] = {
                pp: {
                  new: value[0] || 0,
                  process: value[3] || 0,
                  done: value[6] || 0,
                  outstanding_3days: value[9] || 0
                },
                pr: {
                  new: value[1] || 0,
                  process: value[4] || 0,
                  done: value[7] || 0,
                  outstanding_date_need: value[10] || 0,
                  outstanding_1week: value[11] || 0
                },
                po: {
                  new: value[2] || 0,
                  process: value[5] || 0,
                  done: value[8] || 0,
                  outstanding_3days_po: value[12] || 0,
                  outstanding_1month: value[13] || 0
                }
              };
            } else if (value && typeof value === 'object' && value.pp && value.pr && value.po) {
              // New format: already structured, use as is (key is already id_hotel)
              transformedData[key] = value;
            }
          });
          
          console.log('🟢 getDataCountHotel - Transformed Data:', transformedData);
          console.log('🟢 getDataCountHotel - Keys in transformed data:', Object.keys(transformedData));
          setDataReportbyHotel(transformedData);
        } else {
          console.warn('🟢 getDataCountHotel - Invalid response format, setting null');
          setDataReportbyHotel(null);
        }
      })
      .catch(function (error) {
        console.error('❌ getDataCountHotel - Error fetching hotel data:', error);
        console.error('❌ getDataCountHotel - Error response:', error.response);
        console.error('❌ getDataCountHotel - Error message:', error.message);
        setDataReportbyHotel(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [hcode, token, headers]);

  // Initial load
  useEffect(() => {
    if (token && hcode) {
      getHotels();
    }
  }, [token, hcode, getHotels]);

  // Load data when hotels are loaded
  useEffect(() => {
    console.log('🟡 useEffect - hotelList changed:', {
      token: !!token,
      hcode,
      hotelListLength: hotelList.length,
      hotelList: hotelList
    });
    if (token && hcode && hotelList.length > 0) {
      console.log('🟡 useEffect - Calling getDataCountHotel');
      getDataCountHotel();
    }
  }, [hotelList.length, token, hcode, getDataCountHotel]);

  // Refresh data every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (token && hcode && hotelList.length > 0) {
        getDataCountHotel();
      }
    }, 300000); // 5 minutes

    return () => clearInterval(interval);
  }, [token, hcode, hotelList.length]);

  // Debug: Log current state
  console.log('🟣 Render - Current State:', {
    hotelListLength: hotelList.length,
    hotelList: hotelList,
    dataReportbyHotel: dataReportbyHotel,
    loading,
    loadingHotels,
    selectedTab
  });

  return (
    <Layout title="Daily Document" subtitle="Monitor daily document flow across all hotels">
      <div className="p-4 sm:p-6 md:p-8">
        
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-text-dark">Daily Document</h1>
              <p className="text-sm text-gray-500 mt-1">
                Real-time monitoring of proposals, requests, and purchase orders
              </p>
            </div>
            <button
              onClick={getDataCountHotel}
              disabled={loading}
              className="px-4 py-2 bg-accent-rose text-white text-sm rounded-lg hover:bg-secondary-dark transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Loading Skeleton */}
        {(loadingHotels || loading) && hotelList.length === 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((skeleton) => (
              <div key={skeleton} className="relative bg-gradient-to-br from-primary-dark to-secondary-dark dark:from-gray-700 dark:to-gray-800 rounded-2xl p-5 shadow-xl animate-pulse">
                <div className="space-y-4">
                  {/* Header Skeleton */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="h-5 bg-white/20 dark:bg-white/10 rounded w-32"></div>
                    <div className="h-4 bg-white/20 dark:bg-white/10 rounded w-16"></div>
                  </div>
                  
                  {/* Tab Navigation Skeleton */}
                  <div className="flex gap-1 rounded-lg bg-white/10 backdrop-blur-sm p-1">
                    <div className="h-8 bg-white/20 dark:bg-white/10 rounded-md flex-1"></div>
                    <div className="h-8 bg-white/20 dark:bg-white/10 rounded-md flex-1"></div>
                    <div className="h-8 bg-white/20 dark:bg-white/10 rounded-md flex-1"></div>
                  </div>
                  
                  {/* Content Skeleton - New, Process, Done */}
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <div className="h-3 bg-white/20 dark:bg-white/10 rounded w-4"></div>
                      <div className="h-3 bg-white/20 dark:bg-white/10 rounded w-12"></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 bg-white/20 dark:bg-white/10 rounded w-4"></div>
                      <div className="h-3 bg-white/20 dark:bg-white/10 rounded w-16"></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 bg-white/20 dark:bg-white/10 rounded w-4"></div>
                      <div className="h-3 bg-white/20 dark:bg-white/10 rounded w-12"></div>
                    </div>
                  </div>
                  
                  {/* Outstanding Skeleton */}
                  <div className="border-t border-white/20 pt-3">
                    <div className="h-3 bg-white/20 dark:bg-white/10 rounded w-20 mb-2"></div>
                    <div className="h-10 bg-white/20 dark:bg-white/10 rounded w-16 mb-2"></div>
                    <div className="h-6 bg-white/20 dark:bg-white/10 rounded w-full"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Hotel Cards Grid */}
        {!loadingHotels && hotelList.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {hotelList.map((hotelItem, index) => {
            // Handle nested array structure: hotelItem might be an array or object
            const hotel = Array.isArray(hotelItem) ? hotelItem[0] : hotelItem;
            
            if (!hotel || !hotel.id_hotel) {
              console.warn(`🟣 Rendering Hotel Card - Invalid hotel data at index ${index}:`, hotelItem);
              return null;
            }
            
            // Try both hotel ID and hotel name as keys (API might return either)
            const hotelDataById = dataReportbyHotel?.[hotel.id_hotel];
            const hotelDataByName = dataReportbyHotel?.[hotel.nm_hotel];
            const hotelData = hotelDataById || hotelDataByName;
            
            console.log(`🟣 Rendering Hotel Card - ID: ${hotel.id_hotel}, Name: ${hotel.nm_hotel}`);
            console.log(`🟣 Available keys in dataReportbyHotel:`, Object.keys(dataReportbyHotel || {}));
            console.log(`🟣 hotelDataById:`, hotelDataById);
            console.log(`🟣 hotelDataByName:`, hotelDataByName);
            console.log(`🟣 Final hotelData:`, hotelData);
            
            return (
            <div key={hotel.id_hotel || index} className="relative bg-gradient-to-br from-primary-dark to-secondary-dark dark:bg-gray-600 rounded-2xl p-5 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden group">
              {/* Animated Background */}
              <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Floating Icon */}
              <div className="absolute -bottom-6 -right-6 opacity-10">
                <FileText className="w-32 h-32 text-white" />
              </div>

              <div className="relative z-10">
                {/* Header */}
                <div className="mb-4 flex items-center justify-between gap-1">
                  <p className="text-sm font-bold text-white">
                    {hotel.nm_hotel}
                  </p>
                  <span className="text-xs text-white/70 whitespace-nowrap">
                    {(() => {
                      const date = new Date();
                      const month = date.toLocaleDateString('en-US', { month: 'short' });
                      const day = date.getDate();
                      const year = date.getFullYear();
                      return `${month} ${day}, ${year}`;
                    })()}
                  </span>
                </div>

                {/* Tab Navigation */}
                <div className="flex items-center gap-1 rounded-lg bg-white/10 backdrop-blur-sm p-1 mb-4">
                  <button 
                    onClick={() => setSelectedTab({...selectedTab, [hotel.id_hotel]: 'proposal'})}
                    className={`flex items-center justify-center gap-1.5 w-full rounded-md px-2 py-2 text-xs font-semibold transition-all ${
                      (selectedTab[hotel.id_hotel] || 'proposal') === 'proposal' 
                        ? 'bg-white text-primary-dark shadow-md' 
                        : 'text-white/80 hover:text-white'
                    }`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">PP</span>
                  </button>
                  <button 
                    onClick={() => setSelectedTab({...selectedTab, [hotel.id_hotel]: 'request'})}
                    className={`flex items-center justify-center gap-1.5 w-full rounded-md px-2 py-2 text-xs font-semibold transition-all ${
                      (selectedTab[hotel.id_hotel] || 'proposal') === 'request' 
                        ? 'bg-white text-primary-dark shadow-md' 
                        : 'text-white/80 hover:text-white'
                    }`}
                  >
                    <ClipboardList className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">PR</span>
                  </button>
                  <button 
                    onClick={() => setSelectedTab({...selectedTab, [hotel.id_hotel]: 'order'})}
                    className={`flex items-center justify-center gap-1.5 w-full rounded-md px-2 py-2 text-xs font-semibold transition-all ${
                      (selectedTab[hotel.id_hotel] || 'proposal') === 'order' 
                        ? 'bg-white text-primary-dark shadow-md' 
                        : 'text-white/80 hover:text-white'
                    }`}
                  >
                    <ShoppingCart className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">PO</span>
                  </button>
                </div>

                {/* Tab Content */}
                <div>
                  {/* Purchase Proposal Tab */}
                  {(selectedTab[hotel.id_hotel] || 'proposal') === 'proposal' && (
                    <div>
                      <div className="flex items-end justify-between mb-3">
                        <div className="space-y-1.5">
                          <p className="text-xs text-white/80">
                            <span className="font-bold text-white">{hotelData?.pp?.new || 0}</span> New
                          </p>
                          <p className="text-xs text-white/80">
                            <span className="font-bold text-white">{hotelData?.pp?.process || 0}</span> Process
                          </p>
                          <p className="text-xs text-white/80">
                            <span className="font-bold text-white">{hotelData?.pp?.done || 0}</span> Done
                          </p>
                        </div>
                      </div>
                      <div className="border-t border-white/20 pt-3">
                        <p className="mb-1 text-xs text-white/70">
                          Outstanding
                        </p>
                        <h4 className="mb-2 text-3xl font-bold text-white">
                          {hotelData?.pp?.outstanding_3days || 0}
                        </h4>
                        <span className="inline-block px-2 py-1 bg-accent-rose/30 backdrop-blur-sm rounded-md text-xs text-white font-medium">
                          3 Days after DOO Approve
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Purchase Request Tab */}
                  {(selectedTab[hotel.id_hotel] || 'proposal') === 'request' && (
                    <div>
                      <div className="flex items-end justify-between mb-3">
                        <div className="space-y-1.5">
                          <p className="text-xs text-white/80">
                            <span className="font-bold text-white">{hotelData?.pr?.new || 0}</span> New
                          </p>
                          <p className="text-xs text-white/80">
                            <span className="font-bold text-white">{hotelData?.pr?.process || 0}</span> Process
                          </p>
                          <p className="text-xs text-white/80">
                            <span className="font-bold text-white">{hotelData?.pr?.done || 0}</span> Done
                          </p>
                        </div>
                      </div>
                      <div className="border-t border-white/20 pt-3">
                        <p className="mb-1 text-xs text-white/70">
                          Outstanding
                        </p>
                        <h4 className="mb-2 text-3xl font-bold text-white">
                          {hotelData?.pr?.outstanding_date_need || 0} | {hotelData?.pr?.outstanding_1week || 0}
                        </h4>
                        <span className="inline-block px-2 py-1 bg-accent-rose/30 backdrop-blur-sm rounded-md text-xs text-white font-medium">
                          Date Need &lt; Today | 1 Week after MD Approve
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Purchase Order Tab */}
                  {(selectedTab[hotel.id_hotel] || 'proposal') === 'order' && (
                    <div>
                      <div className="flex items-end justify-between mb-3">
                        <div className="space-y-1.5">
                          <p className="text-xs text-white/80">
                            <span className="font-bold text-white">{hotelData?.po?.new || 0}</span> New
                          </p>
                          <p className="text-xs text-white/80">
                            <span className="font-bold text-white">{hotelData?.po?.process || 0}</span> Process
                          </p>
                          <p className="text-xs text-white/80">
                            <span className="font-bold text-white">{hotelData?.po?.done || 0}</span> Done
                          </p>
                        </div>
                      </div>
                      <div className="border-t border-white/20 pt-3">
                        <p className="mb-1 text-xs text-white/70">
                          Outstanding
                        </p>
                        <h4 className="mb-2 text-3xl font-bold text-white">
                          {hotelData?.po?.outstanding_3days_po || 0} | {hotelData?.po?.outstanding_1month || 0}
                        </h4>
                        <span className="inline-block px-2 py-1 bg-accent-rose/30 backdrop-blur-sm rounded-md text-xs text-white font-medium">
                          3 Days after PO Print | 1 Month Not Receive
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })}
          </div>
        )}

        {/* Empty State */}
        {!loadingHotels && !loading && hotelList.length === 0 && (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <p className="text-gray-500 text-lg mb-2">No Hotels Found</p>
            <p className="text-gray-400 text-sm">Please check your hotel access permissions</p>
          </div>
        )}

        {/* Loading Data Overlay - Show when hotels are loaded but data is still fetching */}
        {!loadingHotels && hotelList.length > 0 && loading && (
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-xl flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-3"></div>
              <p className="text-gray-700 dark:text-gray-300 text-sm">Loading hotel data...</p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Daily;
